import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {MaterialGraph} from "@cm/material-nodes"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Subscription} from "rxjs"
import {defer, filter, map} from "rxjs"

const TD = TypeDescriptors

const loadMaterialRevisionDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    materialId: TD.inlet(TD.Number),
    materialRevisionId: TD.inlet(TD.Nullable(TD.Number)),
    flatten: TD.inlet(TD.Boolean),
    materialGraph: TD.outlet(TD.MaterialGraph),
}

export class LoadMaterialRevision implements NodeClassImpl<typeof loadMaterialRevisionDescriptor, typeof LoadMaterialRevision> {
    static descriptor = loadMaterialRevisionDescriptor
    static uniqueName = "LoadMaterialRevision"
    sceneManager!: Inlet<ISceneManager>
    materialId!: Inlet<number>
    materialRevisionId!: Inlet<number | null>
    flatten!: Inlet<boolean>
    materialGraph!: Outlet<MaterialGraph>

    private pending: Subscription | null = null
    private reloads: Subscription | null = null

    run() {
        this.cleanup()

        if (this.sceneManager === NotReady || this.materialId === NotReady || this.materialRevisionId === NotReady || this.flatten === NotReady) {
            this.materialGraph.emitIfChanged(NotReady)
            return
        }

        const {sceneManager, materialId, materialRevisionId, flatten} = this

        const load = () => {
            this.pending?.unsubscribe()
            this.pending = sceneManager.addTask({
                description: `loadMaterial(${materialId}, ${materialRevisionId}, ${flatten})`,
                task: defer(() => sceneManager.loadMaterial(materialId, materialRevisionId, flatten)).pipe(
                    map((materialGraph) => {
                        this.materialGraph.emitIfChanged(materialGraph)
                    }),
                ),
                critical: true,
            })
        }
        load()
        if (materialRevisionId === null) {
            this.reloads = sceneManager
                .latestMaterialRevisionChanged()
                .pipe(filter((changed) => changed === materialId))
                .subscribe(load)
        }
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
        this.reloads?.unsubscribe()
        this.reloads = null
    }
}
